import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner, Badge } from 'react-bootstrap';
import "../styles/css/Users.css";
import { Niveaux } from '../components/Niveaux';
import {
  hasActiveSubscription, getAbonnementInfo, annulerAbonnement, PLANS
} from '../services/subscriptionService';

const COMMISSION_RATE     = 0.15;
const TEMPS_ECONOMISE_MIN = 15;
const SMIC_HORAIRE        = 11.88;

const UserAccount = () => {
  const [user, setUser]                     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [flashes, setFlashes]               = useState([]);
  const [abonnementInfo, setAbonnementInfo] = useState(null);
  const [stats, setStats]                   = useState(null);
  const [loadingStats, setLoadingStats]     = useState(true);
  const [activeStatTab, setActiveStatTab]   = useState('acheteur');
  const navigate                            = useNavigate();
  const auth                                = getAuth();
  const firestore                           = getFirestore();

  const getCurrentLevel = (points) => {
    let currentLevel = Niveaux[0];
    for (let i = Niveaux.length - 1; i >= 0; i--) {
      if (points >= Niveaux[i].points) { currentLevel = Niveaux[i]; break; }
    }
    return currentLevel;
  };

  const getNextLevelProgress = (points) => {
    const currentLevel = getCurrentLevel(points);
    const nextLevel    = Niveaux.find(n => n.points > currentLevel.points);
    if (!nextLevel) return { percentage: 100, remaining: 0 };
    const passedPoints = points - currentLevel.points;
    const neededPoints = nextLevel.points - currentLevel.points;
    return {
      percentage: Math.min(100, (passedPoints / neededPoints) * 100),
      remaining:  neededPoints - passedPoints,
    };
  };

  const calculerStats = async (userId) => {
    try {
      setLoadingStats(true);
      const maintenant = new Date();
      const debutMois  = new Date(maintenant.getFullYear(), maintenant.getMonth(), 1);

      const qAcheteur  = query(collection(firestore, 'reservations'), where('buyerId', '==', userId), where('status', '==', 'terminée'), orderBy('createdAt', 'desc'));
      const snapAcheteur = await getDocs(qAcheteur);
      const achats       = snapAcheteur.docs.map(d => ({ id: d.id, ...d.data() }));

      const qVendeur   = query(collection(firestore, 'reservations'), where('sellerId', '==', userId), where('status', '==', 'terminée'), orderBy('createdAt', 'desc'));
      const snapVendeur  = await getDocs(qVendeur);
      const ventes       = snapVendeur.docs.map(d => ({ id: d.id, ...d.data() }));

      const getDate = (r) => r.createdAt?.toDate ? r.createdAt.toDate() : new Date(r.createdAt);

      const totalDepenseGlobal    = achats.reduce((s, r) => s + (r.totalPrice || 0), 0);
      const totalDepenseMois      = achats.filter(r => getDate(r) >= debutMois).reduce((s, r) => s + (r.totalPrice || 0), 0);
      const nombreAchatsMois      = achats.filter(r => getDate(r) >= debutMois).length;
      const economiesTotalMin     = achats.length * TEMPS_ECONOMISE_MIN;
      const economiesTotalHeures  = (economiesTotalMin / 60).toFixed(1);

      const totalBrutGlobal  = ventes.reduce((s, r) => s + (r.totalPrice || 0), 0);
      const totalNetGlobal   = totalBrutGlobal * (1 - COMMISSION_RATE);
      const totalBrutMois    = ventes.filter(r => getDate(r) >= debutMois).reduce((s, r) => s + (r.totalPrice || 0), 0);
      const totalNetMois     = totalBrutMois * (1 - COMMISSION_RATE);
      const nombreVentesMois = ventes.filter(r => getDate(r) >= debutMois).length;

      setStats({
        acheteur: {
          transactions: achats, totalDepenseGlobal, totalDepenseMois,
          nombreAchatsGlobal: achats.length, nombreAchatsMois,
          economiesTotalMin, economiesTotalHeures,
          economiesEuros: ((economiesTotalMin / 60) * SMIC_HORAIRE).toFixed(2),
        },
        vendeur: {
          transactions: ventes, totalBrutGlobal, totalNetGlobal,
          totalBrutMois, totalNetMois,
          nombreVentesGlobal: ventes.length, nombreVentesMois,
        },
        pointsTransactions: (achats.length + ventes.length) * 5,
      });
    } catch (err) {
      console.warn("Erreur calcul stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) { navigate('/login'); return; }
        const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = { id: currentUser.uid, ...userDoc.data() };
          setUser(userData);
          const aboInfo = await getAbonnementInfo(currentUser.uid);
          setAbonnementInfo(aboInfo);
          await calculerStats(currentUser.uid);
        } else {
          setFlashes([{ label: 'danger', message: 'Profil utilisateur introuvable' }]);
        }
      } catch (error) {
        console.error("Erreur:", error);
        setFlashes([{ label: 'danger', message: "Erreur lors de la récupération de vos informations" }]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
    // eslint-disable-next-line
  }, []);

  const handleDeleteProfile = () =>
    window.confirm('Êtes-vous sûr de vouloir supprimer votre profil ?');

  const handleAnnulerAbonnement = async () => {
    if (!window.confirm('Voulez-vous vraiment résilier votre abonnement ?')) return;
    try {
      await annulerAbonnement(user.id);
      setAbonnementInfo({ plan: PLANS.FREE, actif: false, expiration: null, jours: 0 });
      setUser(u => ({ ...u, abonnement: PLANS.FREE, abonnementActif: false }));
      alert('Votre abonnement a été résilié.');
    } catch (err) {
      alert("Erreur lors de la résiliation : " + err.message);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return (d?.toDate ? d.toDate() : new Date(d)).toLocaleDateString('fr-FR');
  };

  const masquerAdresse = (adresse) => {
    if (!adresse) return "Zone non renseignée";
    const parts = adresse.split(',');
    if (parts.length >= 2) {
      const rue   = parts[0].replace(/^\d+\s+/, '').trim();
      const ville = parts[parts.length - 1].trim();
      return `${rue}, ${ville}`;
    }
    return adresse.replace(/^\d+\s+/, '').trim();
  };

  const getCouleurNote = (note) => {
    if (!note) return '#6c757d';
    if (note >= 4.5) return '#198754';
    if (note >= 3.5) return '#ffc107';
    return '#dc3545';
  };

  // ── Bandeau vert réutilisable ─────────────────────────────────────────────
  const BandeauVert = ({ texte, icone }) => (
    <div style={{
      backgroundColor: 'rgb(37, 190, 136)',
      padding: 'clamp(24px, 8vw, 60px) 16px',
      margin: 'clamp(32px, 8vw, 80px) 0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <h2 className="text-center text-light mb-0"
        style={{ fontSize: 'clamp(1.2rem, 5vw, 2rem)' }}>
        {texte} <i className={icone}></i>
      </h2>
    </div>
  );

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border" role="status"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mt-5 px-3">
        Veuillez cliquer sur le lien reçu dans votre boîte mail pour accéder à votre compte.{' '}
        <span className='text-muted'>— Possibilité de trouver le lien dans vos spams —</span>
      </div>
    );
  }

  const currentLevel      = getCurrentLevel(user.points || 0);
  const nextLevelProgress = getNextLevelProgress(user.points || 0);
  const estAbonne         = hasActiveSubscription(user);

  const getPlanConfig = () => {
    if (!abonnementInfo || abonnementInfo.plan === PLANS.FREE)
      return { overlay: 'overlay-free',    nom: 'SPOTYFREE', sous: 'aucun abonnement en cours',  couleur: '#6c757d' };
    if (abonnementInfo.plan === PLANS.MENSUEL)
      return { overlay: 'overlay-mensuel', nom: 'SPOTYFLEX', sous: 'Abonnement mensuel actif',   couleur: '#0d6efd' };
    return   { overlay: 'overlay-annuel',  nom: 'SPOTYZEN',  sous: 'Abonnement annuel actif',    couleur: '#07a993' };
  };

  const planConfig = getPlanConfig();

  return (
    <div>
      <div className="container px-3 px-md-4">
        {flashes.map((flash, index) => (
          <div key={index} className={`alert alert-${flash.label}`}>{flash.message}</div>
        ))}

        {/* ── Carte abonnement ──────────────────────────────────────────── */}
        <div className="card position-relative nuage-card">
          <div className={`${planConfig.overlay} d-flex flex-column justify-content-center align-items-center text-center text-light p-3`}>
            <div style={{ fontSize: 'clamp(2rem, 10vw, 4rem)', fontWeight: 700 }}>
              {planConfig.nom}
            </div>
            <div style={{ fontSize: 'clamp(0.9rem, 3vw, 1.5rem)', fontWeight: 600 }}>
              {planConfig.sous}
            </div>
            {estAbonne && abonnementInfo?.jours > 0 && (
              <div style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1.1rem)', marginTop: '8px' }}>
                Expire dans <strong>{abonnementInfo.jours}</strong> jour{abonnementInfo.jours > 1 ? 's' : ''}
              </div>
            )}
          </div>

          <h3 className="text-center bg-secondary-subtle border py-3"
            style={{ fontSize: 'clamp(0.95rem, 3vw, 1.4rem)' }}>
            {abonnementInfo?.plan === PLANS.FREE    ? 'Formule sans abonnement' :
             abonnementInfo?.plan === PLANS.MENSUEL ? 'Abonnement mensuel SPOTYFLEX' :
             'Abonnement annuel SPOTYZEN'}
          </h3>

          <div className="card-body">
            {/* Fonctionnalités disponibles */}
            {[
              { icon: 'fa-location-crosshairs', text: 'Se localiser 24H / 24 | 7jours / 7' },
              { icon: 'fa-square-parking',      text: 'Chercher des stationnements disponibles' },
              { icon: 'fa-bell',                text: "Signaler la libération d'un stationnement" },
              { icon: 'fa-calendar-days',       text: 'Réserver des stationnements vacants' },
            ].map(({ icon, text }, i) => (
              <div key={i} className={`row ${estAbonne ? 'row-mensuel' : 'row-freemium'} py-3`}>
                <div className="col-2 col-md-3 offset-1 offset-md-2 mb-2">
                  <i className={`bi bi-check2-circle fs-2 ${estAbonne ? 'color-circle-mensuel' : 'color-circle-freemium'}`}></i>
                </div>
                <div className="col">
                  <p style={{ fontSize: 'clamp(0.88rem, 3vw, 1.1rem)', marginBottom: 0 }}>
                    {text}
                  </p>
                </div>
              </div>
            ))}

            {/* Fonctionnalités abonnés */}
            {[
              { text: "Mettre en vente sa place et gagner de l'argent" },
              { text: 'Gagner rapidement des points et des récompenses' },
            ].map(({ text }, i) => (
              <div key={i}
                className={`row py-3 ${estAbonne ? 'row-mensuel' : 'bg-secondary-subtle text-danger'}`}
                title={estAbonne ? '' : "Fonctionnalité réservée aux abonnés"}>
                <div className="col-2 col-md-3 offset-1 offset-md-2 mb-2">
                  {estAbonne
                    ? <i className="bi bi-check2-circle fs-2 color-circle-mensuel"></i>
                    : <i className="fa-regular fa-circle-xmark fs-2"></i>}
                </div>
                <div className="col">
                  <p style={{ fontSize: 'clamp(0.88rem, 3vw, 1.1rem)', marginBottom: 0 }}>
                    {text}
                  </p>
                </div>
              </div>
            ))}

            {/* Précision informations */}
            <div
              className={`row py-3 ${estAbonne ? 'row-mensuel' : 'bg-secondary-subtle text-danger'}`}
              title={estAbonne ? '' : "Fonctionnalité réservée aux abonnés"}>
              <div className="col-2 col-md-3 offset-1 offset-md-2 mb-2">
                {estAbonne
                  ? <i className="bi bi-check2-circle fs-2 color-circle-mensuel"></i>
                  : <i className="fa-regular fa-circle-xmark fs-2"></i>}
              </div>
              <div className="col">
                <p style={{ fontSize: 'clamp(0.88rem, 3vw, 1.1rem)', marginBottom: '8px' }}>
                  Précision des informations
                </p>
                <ul style={{ fontSize: 'clamp(0.82rem, 2.5vw, 1rem)', paddingLeft: '18px' }}>
                  <li>voie publique</li>
                  <li>parking souterrain</li>
                  <li>parking centre commercial</li>
                  <li>étage du parking</li>
                  <li>numéro de place</li>
                </ul>
              </div>
            </div>

            {/* Info abonnement actif */}
            {estAbonne && abonnementInfo && (
              <div className="p-3 mb-3 rounded" style={{ background: '#e8f8f5', border: '1px solid #07a993' }}>
                <h6 style={{ fontWeight: 700, color: '#07a993' }}>Votre abonnement</h6>
                <p className="mb-1" style={{ fontSize: '0.88rem' }}>
                  <strong>Plan :</strong>{' '}
                  {abonnementInfo.plan === PLANS.MENSUEL ? 'SPOTYFLEX | abonnement mensuel' : 'SPOTYZEN | abonnement annuel'}
                </p>
                {abonnementInfo.debut && (
                  <p className="mb-1" style={{ fontSize: '0.88rem' }}>
                    <strong>Depuis le :</strong>{' '}
                    {new Date(abonnementInfo.debut).toLocaleDateString('fr-FR')}
                  </p>
                )}
                {abonnementInfo.expiration && (
                  <p className="mb-1" style={{ fontSize: '0.88rem' }}>
                    <strong>Expire le :</strong>{' '}
                    {new Date(abonnementInfo.expiration).toLocaleDateString('fr-FR')}{' '}
                    <span style={{
                      color: abonnementInfo.jours <= 7 ? '#dc3545' : '#198754',
                      fontWeight: 600,
                    }}>
                      ({abonnementInfo.jours} jour{abonnementInfo.jours > 1 ? 's' : ''} restant{abonnementInfo.jours > 1 ? 's' : ''})
                    </span>
                  </p>
                )}
                <button className="btn btn-outline-danger btn-sm mt-2 w-100"
                  onClick={handleAnnulerAbonnement}>
                  Résilier mon abonnement
                </button>
              </div>
            )}

            {/* {!estAbonne && (
              <div className="d-flex justify-content-center mt-3">
                <Link to="/abonnements"
                  className="btn btn-success w-100"
                  style={{ padding: '12px', fontWeight: 700, fontSize: '0.95rem', maxWidth: '400px' }}>
                  Découvrir nos abonnements premium
                </Link>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* ── Bandeau Tableau de bord ───────────────────────────────────────── */}
      <BandeauVert texte="Tableau de bord" icone="bi bi-bar-chart-fill" />

      <div className="container px-3 px-md-4 mb-5">
        {loadingStats ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: '#07a993' }} />
            <p className="mt-2 text-muted">Chargement de vos statistiques...</p>
          </div>
        ) : stats ? (
          <>
            {/* ── Onglets ─────────────────────────────────────────────── */}
            <div className="d-flex gap-2 mb-4 flex-wrap">
              {[
                { key: 'acheteur', label: '🛒 Acheteur', count: stats.acheteur.nombreAchatsGlobal },
                { key: 'vendeur',  label: '💰 Vendeur',  count: stats.vendeur.nombreVentesGlobal  },
              ].map(({ key, label, count }) => (
                <button key={key} onClick={() => setActiveStatTab(key)}
                  style={{
                    flex: '1 1 140px',
                    padding: '10px 16px', borderRadius: '8px',
                    border: `2px solid ${activeStatTab === key ? '#07a993' : '#dee2e6'}`,
                    background: activeStatTab === key ? '#e8f8f5' : '#fff',
                    fontWeight: activeStatTab === key ? 700 : 400,
                    cursor: 'pointer', fontSize: '0.9rem',
                    minHeight: '44px',
                    transition: 'all 0.15s ease',
                  }}>
                  {label}
                  {count > 0 && (
                    <Badge bg={activeStatTab === key ? 'success' : 'secondary'} className="ms-2">
                      {count}
                    </Badge>
                  )}
                </button>
              ))}
            </div>

            {/* ── Vue ACHETEUR ────────────────────────────────────────── */}
            {activeStatTab === 'acheteur' && (
              <>
                <div className="row g-3 mb-4">
                  {[
                    { label: 'Transactions ce mois', value: stats.acheteur.nombreAchatsMois,           unit: 'réservations',                                    color: '#0d6efd', bg: '#e8f0fe', icon: '📅' },
                    { label: 'Dépenses ce mois',     value: stats.acheteur.totalDepenseMois.toFixed(2), unit: '€',                                               color: '#dc3545', bg: '#fce8e8', icon: '💳' },
                    { label: 'Total dépenses',       value: stats.acheteur.totalDepenseGlobal.toFixed(2), unit: '€ depuis le début',                             color: '#6c757d', bg: '#f8f9fa', icon: '📊' },
                    { label: 'Temps économisé',      value: stats.acheteur.economiesTotalMin,           unit: `min (~${stats.acheteur.economiesTotalHeures}h)`,  color: '#198754', bg: '#d1e7dd', icon: '⏱️' },
                  ].map(({ label, value, unit, color, bg, icon }) => (
                    <div key={label} className="col-6 col-lg-3">
                      <div className="p-3 rounded h-100" style={{ background: bg, border: `1px solid ${color}22` }}>
                        <div style={{ fontSize: '1.3rem', marginBottom: '4px' }}>{icon}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6c757d', marginBottom: '4px' }}>{label}</div>
                        <div style={{ fontSize: 'clamp(1rem, 4vw, 1.4rem)', fontWeight: 700, color }}>
                          {value}
                          <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '3px' }}>{unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Historique achats */}
                {stats.acheteur.transactions.length === 0 ? (
                  <div className="text-center py-4" style={{
                    border: '2px dashed #dee2e6', borderRadius: '12px', background: '#f8f9fa'
                  }}>
                    <div style={{ fontSize: '2.5rem' }}>🛒</div>
                    <p className="mt-2 text-muted">Aucun achat effectué pour le moment.</p>
                    <Link to="/reserver-place" className="btn btn-sm btn-outline-primary mt-1">
                      Réserver une place
                    </Link>
                  </div>
                ) : (
                  <>
                    <h6 style={{ fontWeight: 700, marginBottom: '12px' }}>
                      Historique des achats ({stats.acheteur.transactions.length})
                    </h6>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {stats.acheteur.transactions.map(t => (
                        <div key={t.id} className="p-3 mb-2 rounded" style={{
                          border: '1px solid #dee2e6', background: '#fff',
                          borderLeft: '4px solid #0d6efd',
                        }}>
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-word' }}>
                                📍 {masquerAdresse(t.location)}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>
                                {formatDate(t.createdAt)}{t.reservationTime && ` · ${t.reservationTime}`}
                              </div>
                            </div>
                            <div className="text-end" style={{ flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, color: '#dc3545', fontSize: '0.95rem' }}>
                                -{(t.totalPrice || 0).toFixed(2)}€
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#198754' }}>
                                ~{TEMPS_ECONOMISE_MIN} min économisées
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Vue VENDEUR ─────────────────────────────────────────── */}
            {activeStatTab === 'vendeur' && (
              <>
                <div className="row g-3 mb-4">
                  {[
                    { label: 'Ventes ce mois',        value: stats.vendeur.nombreVentesMois,              unit: 'ventes',                                        color: '#07a993', bg: '#e8f8f5', icon: '📅' },
                    { label: 'Gains nets ce mois',    value: stats.vendeur.totalNetMois.toFixed(2),       unit: `€ (après ${COMMISSION_RATE * 100}% commission)`, color: '#198754', bg: '#d1e7dd', icon: '💵' },
                    { label: 'Gains nets totaux',     value: stats.vendeur.totalNetGlobal.toFixed(2),     unit: '€ depuis le début',                             color: '#07a993', bg: '#e8f8f5', icon: '🏆' },
                    { label: 'Total brut encaissé',   value: stats.vendeur.totalBrutGlobal.toFixed(2),    unit: '€ brut total',                                  color: '#6c757d', bg: '#f8f9fa', icon: '📊' },
                    { label: 'Commissions SpotyPark', value: (stats.vendeur.totalBrutGlobal * COMMISSION_RATE).toFixed(2), unit: `€ (${COMMISSION_RATE * 100}%)`, color: '#dc3545', bg: '#fce8e8', icon: '📋' },
                  ].map(({ label, value, unit, color, bg, icon }) => (
                    <div key={label} className="col-6 col-lg-4">
                      <div className="p-3 rounded h-100" style={{ background: bg, border: `1px solid ${color}22` }}>
                        <div style={{ fontSize: '1.3rem', marginBottom: '4px' }}>{icon}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6c757d', marginBottom: '4px' }}>{label}</div>
                        <div style={{ fontSize: 'clamp(1rem, 4vw, 1.4rem)', fontWeight: 700, color }}>
                          {value}
                          <span style={{ fontSize: '0.72rem', fontWeight: 400, marginLeft: '3px' }}>{unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Note vendeur */}
                {(user.averageRating > 0 || user.ratingsReceived > 0) && (
                  <div className="p-3 rounded mb-4" style={{
                    background: '#f8f9fa', border: '1px solid #dee2e6',
                    display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                  }}>
                    <div style={{ textAlign: 'center', minWidth: '70px' }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: 700, color: getCouleurNote(user.averageRating) }}>
                        {(user.averageRating || 0).toFixed(1)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>sur 5</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.1rem', letterSpacing: '2px' }}>
                        {'★'.repeat(Math.round(user.averageRating || 0))}
                        {'☆'.repeat(5 - Math.round(user.averageRating || 0))}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>
                        Basé sur {user.ratingsReceived || 0} avis
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <Badge bg={
                        (user.averageRating || 0) >= 4.5 ? 'success' :
                        (user.averageRating || 0) >= 3.5 ? 'warning' : 'danger'
                      }>
                        {(user.averageRating || 0) >= 4.5 ? '⭐ Excellent' :
                         (user.averageRating || 0) >= 3.5 ? '👍 Bon' : '⚠️ À améliorer'}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Historique ventes */}
                {stats.vendeur.transactions.length === 0 ? (
                  <div className="text-center py-4" style={{
                    border: '2px dashed #dee2e6', borderRadius: '12px', background: '#f8f9fa'
                  }}>
                    <div style={{ fontSize: '2.5rem' }}>💰</div>
                    <p className="mt-2 text-muted">Aucune vente effectuée pour le moment.</p>
                    {estAbonne ? (
                      <Link to="/vendre-place" className="btn btn-sm btn-outline-success mt-1">
                        Mettre une place en vente
                      </Link>
                    ) : (
                      <Link to="/abonnements" className="btn btn-sm btn-warning mt-1">
                        S'abonner pour vendre
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    <h6 style={{ fontWeight: 700, marginBottom: '12px' }}>
                      Historique des ventes ({stats.vendeur.transactions.length})
                    </h6>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {stats.vendeur.transactions.map(t => {
                        const prixBrut   = t.totalPrice || 0;
                        const commission = prixBrut * COMMISSION_RATE;
                        const prixNet    = prixBrut * (1 - COMMISSION_RATE);
                        return (
                          <div key={t.id} className="p-3 mb-2 rounded" style={{
                            border: '1px solid #dee2e6', background: '#fff',
                            borderLeft: '4px solid #07a993',
                          }}>
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-word' }}>
                                  📍 {masquerAdresse(t.location)}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>
                                  {formatDate(t.createdAt)}{t.reservationTime && ` · ${t.reservationTime}`}
                                </div>
                              </div>
                              <div className="text-end" style={{ flexShrink: 0 }}>
                                <div style={{ fontWeight: 700, color: '#198754', fontSize: '0.95rem' }}>
                                  +{prixNet.toFixed(2)}€ net
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                                  Brut : {prixBrut.toFixed(2)}€ — Commission : {commission.toFixed(2)}€
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Gains points ─────────────────────────────────────────── */}
            <div className="mt-4 p-3 rounded" style={{
              background: 'linear-gradient(135deg, #07a993 0%, #0d6efd 100%)',
              color: '#fff', borderRadius: '12px',
            }}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>Points via transactions</div>
                  <div style={{ fontSize: 'clamp(1.4rem, 5vw, 2rem)', fontWeight: 700 }}>
                    {stats.pointsTransactions} pts
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>Total de vos points</div>
                  <div style={{ fontSize: 'clamp(1.4rem, 5vw, 2rem)', fontWeight: 700 }}>
                    {(user.points || 0).toLocaleString()} pts
                  </div>
                </div>
                <Link to="/recompenses" className="btn btn-light btn-sm"
                  style={{ fontWeight: 600, color: '#07a993' }}>
                  🎁 Voir mes récompenses
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-muted" style={{ fontSize: '1.1rem' }}>
            Aucune donnée de transaction.
          </div>
        )}
      </div>

      {/* ── Bandeau Palmarès ──────────────────────────────────────────────── */}
      <BandeauVert texte="Palmarès" icone="fa-solid fa-award" />

      <div className="container px-3 px-md-4">
        <div className="row g-3">

          {/* Tableau niveau actuel */}
          <div className="col-12 col-lg-5">
            <div className="table-responsive">
              <table className="table table-striped mt-2 border">
                <thead>
                  <tr className="text-center">
                    <th colSpan="2" className="text-danger"
                      style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)' }}>
                      Votre niveau actuel <i className="bi bi-bar-chart-line"></i>
                    </th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: 'clamp(0.82rem, 2.5vw, 0.95rem)' }}>
                  <tr>
                    <th scope="row">Statut</th>
                    <td className="text-capitalize fw-bold">
                      {currentLevel.niveau}
                      <img src={currentLevel.badge} alt="Badge niveau"
                        className="ms-2" style={{ width: '28px' }} />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Points</th>
                    <td>
                      <div className="d-flex justify-content-between flex-wrap gap-1">
                        <span>{(user.points || 0).toLocaleString()} pts</span>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                          Prochain : {currentLevel.points.toLocaleString()} pts
                        </span>
                      </div>
                      <div className="progress mt-2" style={{ height: '8px' }}>
                        <div className="progress-bar bg-success" role="progressbar"
                          style={{ width: `${nextLevelProgress.percentage}%` }} />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Récompenses</th>
                    <td>
                      <ul className="list-unstyled mb-0">
                        {currentLevel.gains.map((gain, index) => (
                          <li key={index} className="mb-1" style={{ fontSize: '0.82rem' }}>
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            {gain}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Étoiles</th>
                    <td>
                      <div className="d-flex align-items-center flex-wrap gap-1">
                        <span className="text-warning">
                          {'★'.repeat(Math.floor((user.totalStars || 0) / 20))}
                          {'☆'.repeat(5 - Math.floor((user.totalStars || 0) / 20))}
                        </span>
                        <span style={{ fontSize: '0.82rem' }}>({user.totalStars || 0})</span>
                      </div>
                      <small className="text-muted">100 étoiles = 100 points</small>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th style={{ fontSize: '0.85rem' }}>Comment obtenir des points</th>
                    <td>
                      <Link to="/recompenses"
                        className={`btn btn-success w-100 btn-sm ${(user.points || 0) === 0 ? 'disabled' : ''}`}>
                        Consulter
                      </Link>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Tableau progression niveaux */}
          <div className="col-12 col-lg-7">
            <div className="table-responsive">
              <table className="table table-striped mt-2 border"
                style={{ fontSize: 'clamp(0.72rem, 2vw, 0.88rem)', minWidth: '380px' }}>
                <thead>
                  <tr className="text-center">
                    <th colSpan={Niveaux.length} className="text-danger">
                      Progression des niveaux
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" style={{ whiteSpace: 'nowrap' }}>Niveau</th>
                    {Niveaux.map((niveau) => (
                      <td key={niveau.id} className={`text-center ${(user.points || 0) >= niveau.points ? 'fw-bold text-success' : ''}`}>
                        {niveau.id}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={{ whiteSpace: 'nowrap' }}>Statut</th>
                    {Niveaux.map((niveau) => (
                      <td key={niveau.id} className={`text-center ${(user.points || 0) >= niveau.points ? 'fw-bold text-success' : ''}`}>
                        {niveau.niveau}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={{ whiteSpace: 'nowrap' }}>Points</th>
                    {Niveaux.map((niveau) => (
                      <td key={niveau.id} className="text-center">
                        {niveau.points.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={{ whiteSpace: 'nowrap' }}>Badge</th>
                    {Niveaux.map((niveau) => (
                      <td key={niveau.id} className="text-center">
                        <img src={niveau.badge} alt={`Badge ${niveau.niveau}`}
                          style={{
                            width: 'clamp(24px, 5vw, 40px)',
                            filter:  (user.points || 0) >= niveau.points ? 'none' : 'grayscale(100%)',
                            opacity: (user.points || 0) >= niveau.points ? 1 : 0.6,
                          }} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bandeau Gestion du compte ─────────────────────────────────────── */}
      <BandeauVert texte="Gestion du compte" icone="bi bi-clipboard-check" />

      <div className="container px-3 px-md-4">

        {/* Photo + infos profil */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-lg-5">
                <img
                  src={user.photoProfil}
                  className="img-fluid img-thumbnail"
                  style={{
                    width: '100%',
                    height: 'clamp(200px, 50vw, 400px)',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                  alt="profil"
                />
              </div>
              <div className="col-12 col-lg-7">
                <div className="table-responsive">
                  <table className="table table-bordered" style={{ fontSize: 'clamp(0.82rem, 2.5vw, 0.95rem)' }}>
                    <tbody>
                      {[
                        { label: 'Prénom',             value: user.prenom          },
                        { label: 'Nom',                value: user.nom             },
                        { label: 'Email',              value: user.email           },
                        { label: 'Mot de passe',       value: '**********'         },
                        { label: 'Pseudonyme',         value: user.pseudo          },
                        { label: 'Date de naissance',  value: user.dateNaissance   },
                        { label: 'Numéro Mobile',      value: user.telephone       },
                        { label: 'Langues',            value: user.langue          },
                        { label: 'Type de véhicule',   value: user.typeVehicule    },
                        { label: 'Marque du véhicule', value: user.marqueVehicule  },
                        { label: 'Immatriculation',    value: user.immatriculation },
                      ].map(({ label, value }) => (
                        <tr key={label}>
                          <th scope="row" style={{ whiteSpace: 'nowrap' }}>{label} :</th>
                          <td style={{ wordBreak: 'break-word' }}>
                            {value}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="2" className="text-center">
                          <Link to="/changeProfil" className="btn btn-warning w-100">
                            Modifier mon profil
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions compte */}
        <div className="d-flex flex-column gap-3 mb-5">

          {/* Parrainage */}
          <div className="d-flex justify-content-between align-items-center p-3 rounded"
            style={{ border: '1px solid #dee2e6', background: '#fff' }}>
            <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>
              🎁 Parrainez vos proches
            </span>
            <Link to="/parrainage" className="btn btn-success px-4">
              <i className='bi bi-gift-fill'></i>
            </Link>
          </div>

          {/* Mot de passe */}
          <div style={{
            border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden',
          }}>
            <div className="text-center text-light py-2"
              style={{ backgroundColor: 'rgb(37, 190, 136)', fontSize: '0.92rem', fontWeight: 600 }}>
              Modifier mot de passe
            </div>
            <div className="d-flex justify-content-between align-items-center p-3">
              <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>
                Changer mon mot de passe
              </span>
              <Link to="/changePassword" className="btn btn-warning px-4">
                <i className='bi bi-arrow-clockwise'></i>
              </Link>
            </div>
          </div>

          {/* Supprimer compte */}
          <div style={{
            border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden',
          }}>
            <div className="text-center text-light py-2"
              style={{ backgroundColor: 'rgb(37, 190, 136)', fontSize: '0.92rem', fontWeight: 600 }}>
              Supprimer mon compte
            </div>
            <div className="d-flex justify-content-between align-items-center p-3">
              <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>
                Supprimer définitivement
              </span>
              <Link to="/supprimerCompte" className="btn btn-danger px-4"
                onClick={handleDeleteProfile}>
                <i className='bi bi-trash-fill'></i>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserAccount;